import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Link } from 'react-scroll';
import { motion, AnimatePresence } from 'framer-motion';
import { trackCTA } from '../utils/analytics';
import { useLanguage } from '../context/LanguageContext';
import { content } from '../i18n/content';

const LanguageToggle = ({ language, toggleLanguage, className = '' }) => (
  <button
    type="button"
    onClick={() => {
      toggleLanguage();
      trackCTA('language_toggle', language === 'es' ? 'es_to_en' : 'en_to_es');
    }}
    aria-label="Toggle language"
    className={`px-3 py-1.5 rounded-full border border-white/20 text-white/70 text-xs font-semibold tracking-widest hover:border-secondary hover:text-secondary transition-colors duration-300 cursor-pointer ${className}`}
  >
    {language === 'es' ? 'EN' : 'ES'}
  </button>
);

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const { language, toggleLanguage } = useLanguage();
  const t = content[language].navbar;

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menu when navigating
  const closeMenu = () => setMenuOpen(false);

  const mobileMenuVariants = {
    hidden: { opacity: 0, y: -10, scale: 0.98 },
    visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.2, ease: 'easeOut' } },
    exit: { opacity: 0, y: -10, scale: 0.98, transition: { duration: 0.15 } }
  };

  return (
    <>
      <motion.nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled || menuOpen ? 'bg-dark-navy/98 backdrop-blur-md shadow-lg' : 'bg-transparent'
        }`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          {/* Logo */}
          {isHomePage ? (
            <Link
              to="hero"
              smooth={true}
              duration={500}
              className="cursor-pointer flex items-center"
              onClick={closeMenu}
            >
              <img
                src="/assets/logo/golden_eye_logo.png"
                alt="DigitalFlow"
                className="h-16 w-auto object-contain"
              />
            </Link>
          ) : (
            <RouterLink
              to="/"
              className="cursor-pointer flex items-center"
              onClick={closeMenu}
            >
              <img
                src="/assets/logo/golden_eye_logo.png"
                alt="DigitalFlow"
                className="h-16 w-auto object-contain"
              />
            </RouterLink>
          )}

          {/* Desktop Navigation Links */}
          <div className="hidden md:flex items-center gap-6">
            {isHomePage ? (
              <>
                <Link
                  to="trust"
                  smooth={true}
                  duration={500}
                  className="text-light-gray hover:text-secondary transition-colors duration-300 cursor-pointer font-medium"
                >
                  {t.work}
                </Link>
                {/* <RouterLink to="/catalogo" className="text-light-gray hover:text-secondary transition-colors duration-300 font-medium">Catálogo</RouterLink> */}
                <LanguageToggle language={language} toggleLanguage={toggleLanguage} />
                <Link
                  to="contact"
                  smooth={true}
                  duration={500}
                  onClick={() => {
                    closeMenu();
                    trackCTA('navbar', 'Agendar diagnóstico');
                  }}
                  className="px-6 py-2.5 bg-secondary text-white font-label font-medium rounded-full shadow-[0_0_20px_rgba(0,123,255,0.2)] hover:shadow-[0_0_30px_rgba(0,123,255,0.4)] transition-all duration-300 cursor-pointer"
                >
                  {t.cta}
                </Link>
              </>
            ) : (
              <>
                <RouterLink
                  to="/"
                  className="text-light-gray hover:text-secondary transition-colors duration-300 font-medium"
                >
                  {t.home}
                </RouterLink>
                <LanguageToggle language={language} toggleLanguage={toggleLanguage} />
              </>
            )}
          </div>

          {/* Mobile: language toggle + hamburger */}
          <div className="md:hidden flex items-center gap-3">
            <LanguageToggle language={language} toggleLanguage={toggleLanguage} />
            <button
              className="text-white p-2 rounded-lg hover:bg-white/10 transition-colors"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Toggle menu"
            >
              <motion.div
                animate={menuOpen ? 'open' : 'closed'}
                className="w-6 h-5 flex flex-col justify-between"
              >
                <motion.span
                  variants={{
                    closed: { rotate: 0, y: 0 },
                    open: { rotate: 45, y: 9 }
                  }}
                  className="w-full h-0.5 bg-secondary block origin-center transition-all"
                />
                <motion.span
                  variants={{
                    closed: { opacity: 1 },
                    open: { opacity: 0 }
                  }}
                  className="w-full h-0.5 bg-secondary block"
                />
                <motion.span
                  variants={{
                    closed: { rotate: 0, y: 0 },
                    open: { rotate: -45, y: -9 }
                  }}
                  className="w-full h-0.5 bg-secondary block origin-center transition-all"
                />
              </motion.div>
            </button>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        <AnimatePresence>
          {menuOpen && (
            <motion.div
              variants={mobileMenuVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="md:hidden bg-dark-navy/98 backdrop-blur-md border-t border-white/5 px-6 pb-8 pt-4"
            >
              <div className="flex flex-col gap-6">
                {isHomePage ? (
                  <>
                    <Link
                      to="trust"
                      smooth={true}
                      duration={500}
                      onClick={closeMenu}
                      className="text-white/80 hover:text-secondary transition-colors duration-300 cursor-pointer font-medium text-lg py-2 border-b border-white/5"
                    >
                      {t.work}
                    </Link>
                    <Link
                      to="contact"
                      smooth={true}
                      duration={500}
                      onClick={() => {
                        closeMenu();
                        trackCTA('navbar_mobile', 'Agendar diagnóstico');
                      }}
                      className="w-full text-center px-6 py-4 bg-secondary text-white font-label font-medium rounded-full shadow-[0_0_20px_rgba(0,123,255,0.2)] hover:shadow-[0_0_30px_rgba(0,123,255,0.4)] transition-all duration-300 cursor-pointer"
                    >
                      {t.cta}
                    </Link>
                  </>
                ) : (
                  <RouterLink
                    to="/"
                    onClick={closeMenu}
                    className="text-white/80 hover:text-secondary transition-colors duration-300 font-medium text-lg py-2"
                  >
                    {t.home}
                  </RouterLink>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>
    </>
  );
};

export default Navbar;
